// bookie-olive-preview — bk16 "Zaytun" graduation card-preview.
//
// A classical olive-and-paper taklifnoma. Outer frame is a brushed olive
// gradient; inside, an ivory card carries a laurel-wreath medallion as
// the focal element (in place of a photo), the graduate's name in
// Cormorant Garamond, and the date + venue caption. Follows the
// rose-gold pattern beat-for-beat.
import { Photo, type LayoutCtx } from "./_shared";

export function BookieOlivePreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const FOIL = `linear-gradient(135deg, ${accent} 0%, #C9B47A 22%, #E5D5A2 45%, #C9B47A 65%, ${accent} 100%)`;
  const yearMatch = date.match(/(\d{4})/);
  const year = yearMatch ? yearMatch[1] : "";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Laurel-wreath medallion — the focal element. */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOIL,
              padding: "5%",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
                boxShadow: `inset 0 0 0 1px ${accent}55`,
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 45%, ${accent}22, ${bg})`,
                      }}
                    />
                  }
                />
                {/* Laurel wreath SVG on top of the photo/wash. */}
                <svg
                  viewBox="0 0 100 100"
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  aria-hidden
                >
                  <g fill={accent} opacity="0.85">
                    {/* Left laurel — 6 paired leaves around an arc */}
                    {[
                      { cx: 22, cy: 70, rot: 65 },
                      { cx: 18, cy: 56, rot: 50 },
                      { cx: 18, cy: 42, rot: 30 },
                      { cx: 22, cy: 30, rot: 15 },
                      { cx: 30, cy: 22, rot: 0 },
                      { cx: 40, cy: 18, rot: -10 },
                    ].map((l, i) => (
                      <ellipse
                        key={`l${i}`}
                        cx={l.cx}
                        cy={l.cy}
                        rx="5"
                        ry="2"
                        transform={`rotate(${l.rot} ${l.cx} ${l.cy})`}
                      />
                    ))}
                    {/* Right laurel — mirror */}
                    {[
                      { cx: 78, cy: 70, rot: -65 },
                      { cx: 82, cy: 56, rot: -50 },
                      { cx: 82, cy: 42, rot: -30 },
                      { cx: 78, cy: 30, rot: -15 },
                      { cx: 70, cy: 22, rot: 0 },
                      { cx: 60, cy: 18, rot: 10 },
                    ].map((l, i) => (
                      <ellipse
                        key={`r${i}`}
                        cx={l.cx}
                        cy={l.cy}
                        rx="5"
                        ry="2"
                        transform={`rotate(${l.rot} ${l.cx} ${l.cy})`}
                      />
                    ))}
                  </g>
                  {/* Mortarboard at the crown */}
                  <g transform="translate(50 18)" fill={accent}>
                    <path d="M-8 0 L0 -3.5 L8 0 L0 3.5 Z" />
                    <path d="M-4 1.2 V 4 Q 0 5.4 4 4 V 1.2" stroke={accent} strokeWidth="0.8" fill="none" />
                  </g>
                  {/* Year in centre */}
                  {year && (
                    <text
                      x="50"
                      y="55"
                      textAnchor="middle"
                      fontFamily={font}
                      fontSize="14"
                      fontStyle="italic"
                      fill={ink}
                      opacity="0.9"
                    >
                      {year}
                    </text>
                  )}
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Graduate's name (no &). */}
        <div
          style={{
            fontFamily: font,
            fontSize: 28,
            fontWeight: 500,
            lineHeight: 1.05,
            color: ink,
          }}
        >
          {names.replace(/\s*&\s*/g, " ")}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TAKLIFNOMA · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
